import { useMemo, useState } from "react";
import { FaSearch } from "react-icons/fa";

function TrackingSidebar({ selectedId, onSelect, trackingData }) {
  // ✅ UI-only state (no API/logic change)
  const [search, setSearch] = useState("");

  // ✅ UI-only filtering (keeps same data + selection logic)
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return trackingData || [];
    return (trackingData || []).filter((item) =>
      String(item._id || "").toLowerCase().includes(q)
    );
  }, [trackingData, search]);

  return (
    <aside className="w-full sm:w-80 lg:w-96 h-screen border-r bg-white">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/90 backdrop-blur border-b">
        <div className="p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-lg font-extrabold text-slate-900">
                Tracking List
              </h1>
              <p className="text-xs text-slate-500 mt-0.5">
                Select an ID to view tracking details.
              </p>
            </div>

            <span className="shrink-0 inline-flex items-center rounded-full border bg-slate-50 px-2.5 py-1 text-xs font-extrabold text-slate-700">
              {(trackingData?.length || 0) + " items"}
            </span>
          </div>

          {/* Search */}
          <div className="relative mt-4">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search by Tracking ID…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full h-11 pl-9 pr-3 rounded-xl border bg-white text-slate-900 placeholder:text-slate-400
                         focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Result count */}
          <p className="text-xs text-slate-500 mt-2">
            Showing <span className="font-bold text-slate-700">{filtered.length}</span>{" "}
            of <span className="font-bold text-slate-700">{trackingData?.length || 0}</span>
          </p>
        </div>
      </div>

      {/* List */}
      <div className="h-[calc(100vh-128px)] overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="p-4">
            <div className="rounded-2xl border bg-slate-50 p-5 text-center">
              <p className="text-sm font-extrabold text-slate-900">
                No results found
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Try searching with a different ID.
              </p>
            </div>
          </div>
        ) : (
          filtered.map((item) => {
            const isActive = selectedId === item._id;
            const shortId = item._id ? item._id.slice(0, 7) : "—";
            const dateText = item.createdAt
              ? new Date(item.createdAt).toLocaleString()
              : "—";

            return (
              <button
                key={item._id}
                onClick={() => onSelect(item._id)}
                className={`w-full text-left px-4 py-3 border-b transition group ${
                  isActive
                    ? "bg-blue-50/60 border-blue-100"
                    : "bg-white hover:bg-slate-50 border-slate-100"
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-extrabold text-slate-900 truncate">
                      ID #{shortId}...
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5 truncate">
                      {dateText}
                    </p>
                  </div>

                  <span
                    className={`shrink-0 h-2.5 w-2.5 rounded-full ${
                      isActive ? "bg-blue-600" : "bg-slate-300 group-hover:bg-slate-400"
                    }`}
                    aria-hidden="true"
                  />
                </div>
              </button>
            );
          })
        )}
      </div>
    </aside>
  );
}

export default TrackingSidebar;